import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import queryString from 'query-string';
import utils from '@utils';
import { PaymentService } from '@services';
import { Loader, Image } from '@components/common';
import { BULK_PAYMENT_STATUSES, BULK_PAYMENT_JOB_TIMER } from '@constants';

import './ReviewPayment.scss';

let intervalRef;

class ReviewPayment extends Component {
	constructor(props) {
		super(props);
		this.state = {
			show          : false,
			status        : 'pending', // values: pending, loading, success
			transactionId : -1,
		};

		this.initialState = { ...this.state };
	}

	componentWillUnmount() {
		if (intervalRef) {
			clearInterval(intervalRef);
		}
	}

	openModal = () => {
		this.setState({ show : true });
	}

	onCancel = () => {
		const { status } = this.state;
		const { push } = this.props;

		if (status !== 'pending') {
			push('/payments/list/payment');
			return;
		}

		this.resetModal();
	}

	resetModal = () => this.setState(this.initialState)

	onViewReceipt = () => {
		const { transactionId } = this.state;
		const { push } = this.props;

		push({ pathname : `/payments/receipt/${transactionId}`, search : queryString.stringify({ paid : true }) });
	}

	checkBulkPaymentStatus = (jobId) => {
		const checkPaymentRequest = () => {
			PaymentService.verifyBulkPaymentStatus(jobId)
				.then(({ data }) => {
					const StatusId = data ? data.StatusId : -1;

					if (StatusId === BULK_PAYMENT_STATUSES.SUCCEEDED) {
						const transactionId = data ? data.TransactionId : -1;

						clearInterval(intervalRef);
						this.setState({ status : 'success', transactionId });

						utils.notify({
							type    : 'success',
							message : 'Payment complete',
						});
					} else if (StatusId === BULK_PAYMENT_STATUSES.FAILED) {
						clearInterval(intervalRef);
						this.setState({ status : 'pending' });

						utils.notify({
							type    : 'error',
							title   : 'Payment failed',
							message : 'The invoices payment has failed',
						});
					}
				})
				.catch(({ response : { data } }) => {
					utils.notify({
						type    : 'error',
						title   : 'Error',
						message : data ? data.Message : 'Error getting the current status of the payment',
					});
				});
		};

		intervalRef = setInterval(checkPaymentRequest, BULK_PAYMENT_JOB_TIMER);
	}

	onMakePayment = () => {
		const { total, activeInvoiceIds, paymentMethodId } = this.props;

		this.setState({ status : 'loading' });

		PaymentService.makePayment(paymentMethodId, total, activeInvoiceIds)
			.then(({ data }) => {
				utils.notify({
					type    : 'info',
					title   : '',
					message : 'Payment processing',
				});

				this.checkBulkPaymentStatus(data.JobId);
			})
			.catch(({ response : { data } }) => {
				this.setState({ status : 'pending' });
				utils.notify({
					type    : 'error',
					message : data ? data.Message : 'The invoice could not be paid',
				});
			});
	}

	render() {
		const { show, status } = this.state;
		const {
			subTotal,
			taxes,
			total,
		} = this.props;

		return (
			<Modal
				show={show}
				onHide={this.onCancel}
				id="review-payment-modal"
			>
				<Modal.Header closeButton>
					<Modal.Title>{ status === 'success' ? 'Invoice(s) Paid' : 'Review Payment' }</Modal.Title>
				</Modal.Header>
				<Modal.Body className="text-center">
					<Choose>
						<When condition={status === 'pending'}>
							<div id="review-payment-summary">
								<div className="row">
									<div id="review-payment-subtitle" className="col-xs-12">
										<strong>Order Summary</strong>
									</div>
								</div>
								<div className="row-separator" />
								<div className="row">
									<div className="col-xs-6 text-right">Subtotal</div>
									<div className="col-xs-6 text-left">{utils.currencyFormatter(subTotal)}</div>
								</div>
								<div className="row-separator" />
								<div className="row">
									<div className="col-xs-6 text-right">Fees and Taxes</div>
									<div className="col-xs-6 text-left">{utils.currencyFormatter(taxes)}</div>
								</div>
								<div className="row-separator row-separator-onlyTop" />
								<div id="review-payment-total-row" className="row">
									<div className="col-xs-6 text-right">TOTAL</div>
									<div id="review-payment-total" className="col-xs-6 text-left">
										<strong>{utils.currencyFormatter(total)}</strong>
									</div>
								</div>
								<div id="review-payment-pay-btn">
									<button
										type="button"
										className="btn btn-primary"
										onClick={this.onMakePayment}
									>
										Make Payment
									</button>
								</div>
							</div>
						</When>
						<When condition={status === 'loading'}>
							<div id="review-payment-loading">
								<div>
									<Loader active spinnerOnly />
								</div>
								<div id="review-payment-loading-title">Your payment is being processed</div>
								<div id="review-payment-loading-message">
									<div>You will be notified via email when the process is complete.</div>
									<div>You can close this window or wait until the payment finishes.</div>
									<div>
										Thanks for using
										&nbsp;
										<span className="review-payment-loading-message-blue">RKPay</span>
									</div>
								</div>
								<div id="review-payment-loading-action-btn">
									<button
										type="button"
										className="btn btn-primary"
										onClick={this.onCancel}
									>
										Done
									</button>
								</div>
							</div>
						</When>
						<When condition={status === 'success'}>
							<div id="review-payment-success">
								<div id="review-payment-success-checkmark">
									<Image src="/images/checkmark.png" />
								</div>
								<div id="review-payment-success-title">
									<strong>Payment was successful</strong>
								</div>
								<div id="review-payment-success-message">
									<div>We have successfully processed your payment.</div>
									<div>
										Thanks for using
										&nbsp;
										<span className="review-payment-success-message-blue">RKPay</span>
									</div>
								</div>
								<div id="review-payment-success-action-btn">
									<button
										type="button"
										className="btn btn-primary"
										onClick={this.onViewReceipt}
									>
										View Receipt
									</button>
								</div>
							</div>
						</When>
					</Choose>
				</Modal.Body>
				<If condition={status === 'pending'}>
					<Modal.Footer>
						By placing your order, you agree to RollKall&#39;s&nbsp;
						<a href="https://www.rollkall.com/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>
						&nbsp;and&nbsp;
						<a href="https://www.rollkall.com/terms" target="_blank" rel="noreferrer">Terms of Service</a>
					</Modal.Footer>
				</If>
			</Modal>
		);
	}
}

ReviewPayment.propTypes = {
	subTotal         : PropTypes.number,
	taxes            : PropTypes.number,
	total            : PropTypes.number,
	activeInvoiceIds : PropTypes.array,
	paymentMethodId  : PropTypes.string,
	push             : PropTypes.func.isRequired,
};

ReviewPayment.defaultProps = {
	subTotal         : 0,
	taxes            : 0,
	total            : 0,
	activeInvoiceIds : [],
	paymentMethodId  : null,
};

export default ReviewPayment;
